// Generated by Copilot- Start
import React from "react";
import DtvBox from "@directv-digital/dds.ui.dtv-box";
import DtvText from "../../../video-lab/dtv-text";
import CardMedia from "@mui/material/CardMedia";
import { MBL_SIZE, TBL_SIZE, colorPalette } from "@directv-digital/dds.ui.dtv-theme";
import ErrorDisplay from "@directv-digital/dds.ui.error-display";

interface ModalChannelHeaderProps {
  logo?: string | { mobile?: string; tablet?: string; default: string };
  label: string;
  logoAlt?: string;
  /** Logo size preset: 'default' (64x64) or 'large' (116x66) */
  logoSize?: "default" | "large";
  /** Custom logo styles */
  logoStyles?: Record<string, any>;
  /** Custom label styles */
  labelStyles?: Record<string, any>;
  /** Custom container gap */
  gap?: string | Record<string, string>;
}

/**
 * Reusable modal header component showing channel logo and label
 * Supports both simple string logos and responsive picture sources
 * Used across: ChannelModalV2, RsnChannelModalV2, LocalChannelModalV2, MyFreeDtvModalV2, SvodModalV2
 */
const ModalChannelHeader: React.FC<ModalChannelHeaderProps> = ({
  logo,
  label,
  logoAlt,
  logoSize = "default",
  logoStyles = {},
  labelStyles = {},
  gap = {
    xs: "16px",
    sm: "20px"
  }
}) => {
  try {
    // Determine if logo is responsive object or simple string
    const isResponsiveLogo = typeof logo === "object" && logo !== null;
    const logoSrc = isResponsiveLogo ? (logo as { mobile?: string; tablet?: string; default: string }).default : (logo as string);

    // Size presets
    const sizePresets = {
      default: {
        height: "64px",
        width: "64px"
      },
      large: {
        height: "66px",
        width: "116px"
      }
    };

    const logoSizeStyles = sizePresets[logoSize];

    // Base logo styles
    const baseLogoStyles = {
      display: "inline-flex",
      verticalAlign: "middle",
      objectFit: "contain" as const,
      position: "relative" as const,
      ...logoSizeStyles,
      ...logoStyles
    };

    return (
      <DtvBox id="modal-channel-header" sx={{
 display: "flex",
width: "100%",
alignItems: "center",
gap
}}>
        {logo && (
          <>
            {isResponsiveLogo ? (
              // Responsive picture element for SVOD logos
              <picture>
                {(logo as { mobile?: string; tablet?: string; default: string }).mobile && (
                  <source media={MBL_SIZE} srcSet={(logo as { mobile?: string; tablet?: string; default: string }).mobile} />
                )}
                {(logo as { mobile?: string; tablet?: string; default: string }).tablet && (
                  <source media={TBL_SIZE} srcSet={(logo as { mobile?: string; tablet?: string; default: string }).tablet} />
                )}
                <img
                  alt={logoAlt || label}
                  src={(logo as { mobile?: string; tablet?: string; default: string }).default}
                  style={{
                    ...baseLogoStyles,
                    paddingTop: "1.3px",
                    fontSize: "8px",
                    fill: colorPalette?.neutral?.[600]
                  }}
                  onError={e => {
                    if (e.currentTarget.parentElement) {
                      e.currentTarget.parentElement.style.display = "none";
                    }
                  }}
                />
              </picture>
            ) : (
              // Simple CardMedia for channel logos
              <CardMedia
                alt={logoAlt || label}
                component="img"
                loading="lazy"
                src={logoSrc}
                sx={baseLogoStyles}
                onError={e => {
                  if (e.currentTarget.parentElement) {
                    e.currentTarget.parentElement.style.display = "none";
                  }
                }}
              />
            )}
          </>
        )}
        <DtvText content={label} variant="subhead1medium" sx={{
 fontSize: { sm: "30px !important" },
...labelStyles
}} />
      </DtvBox>
    );
  } catch (error) {
    console.error("Error in ModalChannelHeader >> ", error);
    return <ErrorDisplay component="ModalChannelHeader" error={error} />;
  }
};

export default React.memo(ModalChannelHeader);
// Generated by Copilot- End
